<template>
  <div class="mob-business-selection">
    <!-- Header with back button -->
    <div class="btn-back" @click="goBack">
        <span class="btn-back__icon">
          <SvgIcon icon="ic-arr-left" width="16" height="16" color="white" />
        </span>
        {{ trans('Back') }}
      </div>
    <div class="mob-business-selection__header">
     
      <h2 class="mob-business-selection__title">{{ trans('Select Business') }}</h2>
    </div>

    <!-- Search -->
    <div class="mob-business-selection__search">
      <div class="mob-business-selection__search-input">
        <svg class="icon ic-search" width="15" height="15">
          <use xlink:href="@/assets/sprites/sprite.svg#ic-search"></use>
        </svg>
        <input 
          type="text" 
          :placeholder="trans('Search')" 
          v-model="searchQuery"
          class="mob-business-selection__input"
        />
      </div>
    </div>

    <!-- Business List -->
    <div class="mob-business-selection__list">
      <div
        class="mob-business-selection__item"
        v-for="business in filteredBusiness"
        :key="business._id"
        @click="selectBusiness(business._id)"
        :class="{ active: business._id === currentBusiness?._id }"
      >
        <div class="mob-business-selection__item-content">
          <div
            class="mob-business-selection__item-img"
            :style="getBackgroundImageOrColor(business.image!)"
          ></div>
          
          <div class="mob-business-selection__item-info">
            <div class="mob-business-selection__item-name">{{ business.name }}</div>
            <div class="mob-business-selection__item-email">{{ business.email }}</div>
          </div>
        </div>

        <div class="mob-business-selection__item-check" v-if="business._id === currentBusiness?._id">
          <svg class="icon ic-tick" width="8" height="6">
            <use xlink:href="@/assets/sprites/sprite.svg#ic-tick"></use>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { trans } from '@/common/language/translations';
import { computed, ref } from 'vue';
import useAuthStore from '@/store/auth';
import { getBackgroundImageOrColor } from '@/common/utils/image.util';
import { IBusiness } from '@/declare/types/business';
import SvgIcon from '@/components/UI/SvgIcon.vue';

// Props
defineProps<{
  onBack: () => void;
}>();

// Emits
const emit = defineEmits<{
  'back': [];
}>();

const authStore = useAuthStore();
const searchQuery = ref<string>('');

const currentBusiness = computed(() => authStore.currentBusiness);
const businesses = computed(() => authStore.businesses);

const filteredBusiness = computed(() => {
  if (searchQuery.value.trim() === '') {
    return businesses.value;
  }

  return businesses.value?.filter(business =>
    business.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

async function selectBusiness(id: string): Promise<void> {
  try {
    const selectedBusiness = businesses.value?.find((business: IBusiness) => business._id === id);
    if (selectedBusiness) {
      await authStore.setCurrentBusiness(selectedBusiness);
    }
  } catch (error) {
    console.error('Error selecting business:', error);
  } finally {
    goBack();
  }
}

function goBack(): void {
  emit('back');
}
</script>

<style lang="scss" scoped>
.mob-business-selection {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 2.4rem;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 2rem 1.5rem 1rem 1.5rem;
    border-bottom: 1px solid #F0F0F0;
  }

  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.9rem;
    font-weight: 500;
    color: #333333;
    width: fit-content;
    cursor: pointer;
    margin-bottom: 1.2rem;
    position: relative;
    z-index: 11;

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      .icon {
        fill: #333333;
        width: 2.2rem;
        height: 2.2rem;
      }
    }
  }

  &__title {
    font-size: 1.8rem;
    font-weight: 600;
    color: #333333;
    margin: 0;
  }

  &__search {
    padding: 1.5rem;
  }

  &__search-input {
    position: relative;
    display: flex;
    align-items: center;
    background: #F9F9FB;
    border-radius: 1rem;
    padding: 1rem 1.5rem;
    gap: 1rem;

    .icon {
      fill: #7D8592;
      flex-shrink: 0;
    }
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: none;
    background: transparent;
    font-size: 1.4rem;
    color: #333333;
    outline: none;

    &::placeholder {
      color: #7D8592;
    }
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: 0 1.5rem 2rem 1.5rem;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.3rem 1.5rem;
    border-radius: 1rem;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-bottom: 0.5rem;

    &:hover {
      background: #F9F9FB;
    }

    &.active {
      background: #EEF4FF;
    }
  }

  &__item-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    min-width: 0; // Allow flex item to shrink below content size
  }

  &__item-img {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    background-color: #F5F5F5;
    flex-shrink: 0;
  }

  &__item-info {
    flex: 1;
    min-width: 0;
    overflow: hidden; // Allow content to be hidden when truncated
  }

  &__item-name {
    font-size: 1.4rem;
    font-weight: 500;
    color: #333333;
    margin-bottom: 0.2rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%; // Ensure it respects parent container
  }

  &__item-email {
    font-size: 1.2rem;
    color: #7D8592;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__item-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: #3F8CFF;
    border-radius: 50%;
    flex-shrink: 0;

    .icon {
      fill: white;
    }
  }
}
</style>
